// InspectorDataProvider.tsx
import useAppContext from '@/hooks/useAppContext';
import React, { createContext, useState, useCallback, useContext, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/store/store';
import { selectIsOpenInspector, selectDisableEditorState } from '@/store/slices/editor/selector';
import { setIsOpenInspector, setDisableEditorState } from '@/store/slices/editor/action';

export enum  InspectorPanelType {
    Canvas,
    Resize,
    Sticker,
    Outline,
    Shadow,
    Adjust,
    Text,
    FontFamily,
    Image,
    Eraser,
    Fill,
    Filter,
    MagicBg,
    MagicBgCategory,
    RemoveObjects,
    Enhance,
    MultiElement,
    Crop,
    BoostDesign,
    SuggestedDesigns,
    MagicTemplates,
    MagicTemplateCategory,
    CreateBackground
}

export interface InspectorPanelInputData {
    object?: fabric.Object;
    state?: Record<string, any>;
    setState?: (value: any) => void;
    props?: Record<string, any>;
}

export interface InspectorPanelData {
    inspectorType: InspectorPanelType;
    isVisible: boolean;
    inputData?: InspectorPanelInputData;
    addWithAnimation: boolean;
    closeWithAnimation: boolean;
}

export interface PersistentPanelData {
    scrollTop?: number;
    data?: Record<string, any>; // Panel-specific data (e.g., search results for Fill panel)
}

interface InspectorContextType {
    panelsData: InspectorPanelData[];
    setPanelsData: React.Dispatch<React.SetStateAction<InspectorPanelData[]>>;
    addInspector: (type: InspectorPanelType, data?: InspectorPanelInputData, addWithAnimation?: boolean, addMode?: 'replace' | 'append') => void;
    addInspectorHidingAddMenu: (type: InspectorPanelType, data?: InspectorPanelInputData, addWithAnimation?: boolean, addMode?: 'replace' | 'append') => void;
    hideInspector: (type?: InspectorPanelType, addWithAnimation?: boolean) => void;
    hideAllInspectors: (closeWithAnimation?: boolean) => void;
    getOpenInspectorType: () => InspectorPanelType | null;
    updateInspector: (type: InspectorPanelType, data: InspectorPanelInputData) => void;
    removeInspectorInternal: (type: InspectorPanelType) => void;
    isOpenInspector: boolean;
    setIsOpenInspector: (value: boolean) => void;
    updatePersistedData: (inspectorType: InspectorPanelType, newState: PersistentPanelData[]) => void;
    persistedData: Record<InspectorPanelType, PersistentPanelData>;
}

const InspectorContext = createContext<InspectorContextType | undefined>(undefined);

export const useInspector = (): InspectorContextType => {
    const context = useContext(InspectorContext);
    if (!context) {
        throw new Error('useInspector must be used within an InspectorProvider');
    }
    return context;
};

export const InspectorDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useAppDispatch();
    const { popoverActive, setPopoverActive } = useAppContext();
    const [panelsData, setPanelsData] = useState<InspectorPanelData[]>([]);
    const [persistedData, setPersistedData] = useState<Record<InspectorPanelType, PersistentPanelData>>({} as Record<InspectorPanelType, PersistentPanelData>);
    const disableEditorState = useSelector(selectDisableEditorState);

    const updatePersistedData = useCallback((inspectorType: InspectorPanelType, newState: PersistentPanelData[]) => {
        setPersistedData(prev => {
            const updatedData = { ...prev };
            newState.forEach(newItem => {
                const existingData = updatedData[inspectorType] || {};
                updatedData[inspectorType] = {
                    ...existingData,
                    ...newItem,
                    // Deep merge the data field
                    data: {
                        ...existingData.data,
                        ...newItem.data
                    }
                };
            });
            return updatedData;
        });
    }, []);

    const isOpenInspector = useSelector(selectIsOpenInspector);

    const addInspector = useCallback((type: InspectorPanelType, data?: InspectorPanelInputData, addWithAnimation: boolean = true, addMode: 'replace' | 'append' = 'replace') => {
        const isOpenInspector = isOpenInspectorInternal()
        const isPopoverActiveOpen = popoverActive !== null;
        if (isOpenInspector && addMode === 'replace') {
            for (let i = panelsData.length - 1; i >= 0; i--) {
                hideInspector(panelsData[i].inspectorType, false)
            }
        }
        setPopoverActive(null);
        const addWithAnimationInternal = (isOpenInspector || isPopoverActiveOpen) ? false : addWithAnimation;
        if (addMode === 'replace') {
            setPanelsData([{ inspectorType: type, isVisible: true, inputData: data, addWithAnimation: addWithAnimationInternal, closeWithAnimation: true }]);
        }
        else {
            setPanelsData([...panelsData, { inspectorType: type, isVisible: true, inputData: data, addWithAnimation: addWithAnimationInternal, closeWithAnimation: true }]);
        }
    }, [panelsData, setPanelsData, popoverActive, setPopoverActive]);

    const addInspectorHidingAddMenu = useCallback((type: InspectorPanelType, data?: InspectorPanelInputData, addWithAnimation: boolean = true, addMode: 'replace' | 'append' = 'replace') => {
        const isInspectorCurrentlyOpen = isOpenInspectorInternal();
        
        // Close addMenu if inspector is not already open
        if (!isInspectorCurrentlyOpen) {
            dispatch(setDisableEditorState({...disableEditorState, addMenu: 'hidden' }));
        }
        
        // Then open the inspector
        addInspector(type, data, addWithAnimation, addMode);
    }, [addInspector, disableEditorState, dispatch]);


    const hideInspector = useCallback((type: InspectorPanelType, closeWithAnimation: boolean = true) => {
        if (type) {
            setPanelsData(prev =>
                prev.map(item =>
                item.inspectorType === type ? { ...item, isVisible: false, closeWithAnimation: closeWithAnimation } : item
                )
            );
            return
        }

        setPanelsData(prev => {
            // Find the index of the last item in the array
            const index = prev.length - 1;
        
            if (index < 0) return prev; // If the array is empty, return the original array
        
            // Create a new array with the last item's visibility updated to false
            return prev.map((item, idx) =>
            idx === index ? { ...item, isVisible: false, closeWithAnimation: closeWithAnimation } : item
            );
        });    
    }, [panelsData, setPanelsData]);

    const hideAllInspectors = useCallback((closeWithAnimation: boolean = true) => {
        setPanelsData(prev => 
            prev.map(item => ({ ...item, isVisible: false, closeWithAnimation: item.isVisible ? closeWithAnimation : false }))
        );
    }, [setPanelsData]);

    const getOpenInspectorType = useCallback((): InspectorPanelType | null => {
        if (panelsData.length === 0) return null;
        const openPanel = panelsData.find(panel => panel.isVisible);
        return openPanel ? openPanel.inspectorType : null;
    }, [panelsData]);

    const isOpenInspectorInternal = useCallback(() => {
        return panelsData.length > 0 && panelsData.some(panel => panel.isVisible);
    }, [panelsData]);

    useEffect(() => {
        if (isOpenInspectorInternal() !== isOpenInspector) {
            dispatch(setIsOpenInspector(isOpenInspectorInternal()));
        }
    }, [panelsData, setIsOpenInspector, isOpenInspectorInternal]);


    const removeInspectorInternal = useCallback((type: InspectorPanelType) => {
        setPanelsData(prev => {
            const index = prev.map(item => item.inspectorType).lastIndexOf(type);
            if (index === -1) return prev; // If not found, return the original array
        
            // Create a new array without the last occurrence of the specified type
            return [...prev.slice(0, index), ...prev.slice(index + 1)];
        });
    }, [panelsData, setPanelsData]);

    const updateInspector = useCallback((type: InspectorPanelType, inputData: InspectorPanelInputData) => {
        setPanelsData(prevPanelsData => {
            return prevPanelsData.map(panel => {
                if (panel.inspectorType === type) {
                    return { ...panel, inputData: inputData };
                }
                return panel;
            });
        });
    }, [panelsData, setPanelsData]);

    const contextValue = useMemo(
        () => ({
            panelsData,
            setPanelsData,
            addInspector,
            addInspectorHidingAddMenu,
            hideInspector,
            hideAllInspectors,
            getOpenInspectorType,
            updateInspector,
            removeInspectorInternal,
            isOpenInspector,
            setIsOpenInspector,
            updatePersistedData,
            persistedData
        }),
        [panelsData, setPanelsData, addInspector, addInspectorHidingAddMenu, hideInspector, updateInspector, removeInspectorInternal, isOpenInspector, setIsOpenInspector, updatePersistedData, persistedData]
    );

    return (
        <InspectorContext.Provider value={contextValue}>
            {children}
        </InspectorContext.Provider>
    );
};
